import React from "react";
import { Helmet } from "react-helmet";
import { Key, AlertTriangle, Shield, FileText, ChevronRight, Cpu, Lock, BarChart, FileCheck } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/#about" className="dark-nav-link">ABOUT US</a>
        <a href="/services" className="dark-nav-link active">SERVICES</a>
        <a href="/#pricing" className="dark-nav-link">PRICING</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <a href="/#assessment" className="dark-nav-link">CONTACT</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const ServicesPage = () => {
  const recoveryServices = [
    "Seed Phrase Recovery (1–5 Missing Words)",
    "Seed Order Reconstruction (12/24 Words)",
    "Typographical Correction Analysis (1–2 Words)",
    "Derivation Path Scanning (Standard & Extended)",
    "Passphrase Recovery (Dictionary & Pattern-Based Methods)",
    "Password Recovery (Owner-Authorized – CPU/GPU Accelerated)",
    "Partial Key Reconstruction (Client-Provided Fragments)",
    "Brain Wallet Feasibility Analysis",
    "Address-Only Case Assessment (Feasibility Review in Extreme Cases)"
  ];

  const antiScamServices = [
    "Emergency Wallet Migration (30 minutes response)",
    "Deep Hidden-Funds Scan (2M+ paths)",
    "Compromised Wallet Response",
    "Rapid Asset Transfer Guidance"
  ];

  const securityServices = [
    "Wallet Security Audit (basic & full)",
    "Vulnerability Assessment",
    "Best Practices Consulting",
    "Hardware Wallet Setup",
    "Risk Score Analysis (/100)"
  ];

  const forensicServices = [
    "Blockchain Forensic Report",
    "Technical Documentation",
    "Law Enforcement Ready Reports",
    "Case Evidence Preparation",
    "Timeline & Analysis Documentation"
  ];

  return (
    <div className="App">
      <Helmet>
        <title>Crypto Recovery & Security Services | ARCANA CRYPTO UK</title>
        <meta name="description" content="Professional cryptocurrency recovery, anti-scam protection, security audits, and forensic services. Non-custodial, UK-registered. From £200." />
        <link rel="canonical" href="https://arcana-crypto.com/services-seo.html" />
      </Helmet>
      <Navigation />
      
      <section className="section" style={{marginTop: '128px'}}>
        <div className="section-container">
          <h1 className="section-title">Our Services</h1>
          <p className="section-subtitle">Comprehensive Cryptocurrency Recovery & Security Solutions</p>
          
          <div className="section-intro">
            <p>
              Arcana Crypto offers specialized, ethical, and transparent cryptocurrency recovery and 
              security services to individuals and organizations worldwide.
            </p>
            <p style={{marginTop: '20px'}}>
              All services are delivered case-by-case, following a technical assessment, with clear 
              pricing, realistic success probabilities, and no false guarantees.
            </p>
          </div>

          {/* Recovery Services */}
          <div className="service-category">
            <div className="category-header">
              <Key className="category-icon" size={56} />
              <div>
                <h2 className="category-title">RECOVERY SERVICES</h2>
                <p className="category-subtitle">
                  Specialized recovery for wallets with incomplete, incorrect, or inaccessible credentials
                </p>
              </div>
            </div>
            <div className="category-content">
              <p className="category-description">
                Using controlled computational methodologies and proprietary in-house tools, we assist 
                clients who have lost access due to incomplete seed phrases, incorrect word order, 
                forgotten credentials, or derivation path inconsistencies. All recovery work is 
                performed strictly on behalf of the rightful owner and within applicable legal boundaries.
              </p>
              <div className="service-list">
                <h3 className="service-list-title">What's Included:</h3>
                <ul className="column-list">
                  {recoveryServices.map((service, index) => (
                    <li key={index}>{service}</li>
                  ))}
                </ul>
              </div>
              <div className="category-pricing">
                <div className="pricing-badge">FROM £300</div>
                <div className="pricing-details">
                  Pricing varies based on complexity (1-5 missing words, computational requirements)
                </div>
              </div>
              <a href="/#assessment">
                <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
                  REQUEST RECOVERY ASSESSMENT
                </button>
              </a>
            </div>
          </div>

          {/* Anti-Scam Services */}
          <div className="service-category">
            <div className="category-header">
              <AlertTriangle className="category-icon" size={56} />
              <div>
                <h2 className="category-title">ANTI-SCAM & EMERGENCY SERVICES</h2>
                <p className="category-subtitle">
                  Rapid response for active threats and compromised wallets
                </p>
              </div>
            </div>
            <div className="category-content">
              <p className="category-description">
                If your wallet has been compromised or you suspect unauthorized access, immediate 
                action is critical. Our emergency services are designed to protect assets and provide 
                forensic documentation.
              </p>
              <div className="service-list">
                <h3 className="service-list-title">What's Included:</h3>
                <ul className="column-list">
                  {antiScamServices.map((service, index) => (
                    <li key={index}>{service}</li>
                  ))}
                </ul>
              </div>
              <div className="category-pricing">
                <div className="pricing-badge">FROM £300</div>
                <div className="pricing-details">
                  Emergency services: £500 (30 minutes) | Deep scans: £300-800 (2-6 hours)
                </div>
              </div>
              <a href="/#assessment">
                <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
                  REQUEST EMERGENCY SERVICE
                </button>
              </a>
            </div>
          </div>

          {/* Security Services */}
          <div className="service-category">
            <div className="category-header">
              <Shield className="category-icon" size={56} />
              <div>
                <h2 className="category-title">SECURITY SERVICES</h2>
                <p className="category-subtitle">
                  Professional security assessments and preventive measures
                </p>
              </div>
            </div>
            <div className="category-content">
              <p className="category-description">
                Proactive security audits to identify vulnerabilities and prevent future loss. 
                Professional assessments with actionable recommendations.
              </p>
              <div className="service-list">
                <h3 className="service-list-title">What's Included:</h3>
                <ul className="column-list">
                  {securityServices.map((service, index) => (
                    <li key={index}>{service}</li>
                  ))}
                </ul>
              </div>
              <div className="category-pricing">
                <div className="pricing-badge">FROM £200</div>
                <div className="pricing-details">
                  Basic audit: £200 (30-60 min) | Full audit: £500 (detailed report)
                </div>
              </div>
              <a href="/#assessment">
                <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
                  REQUEST SECURITY AUDIT
                </button>
              </a>
            </div>
          </div>

          {/* Forensic Services */}
          <div className="service-category">
            <div className="category-header">
              <FileText className="category-icon" size={56} />
              <div>
                <h2 className="category-title">FORENSIC SERVICES</h2>
                <p className="category-subtitle">
                  Technical documentation for legal and investigative purposes
                </p>
              </div>
            </div>
            <div className="category-content">
              <p className="category-description">
                Professional forensic reports and blockchain analysis for law enforcement, 
                legal proceedings, or investigative documentation.
              </p>
              <div className="service-list">
                <h3 className="service-list-title">What's Included:</h3>
                <ul className="column-list">
                  {forensicServices.map((service, index) => (
                    <li key={index}>{service}</li>
                  ))}
                </ul>
              </div>
              <div className="category-pricing">
                <div className="pricing-badge">FROM £300</div>
                <div className="pricing-details">
                  Basic report: £300 (1-2 hours) | Full report + consultation: £1,000
                </div>
              </div>
              <a href="/#assessment">
                <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
                  REQUEST FORENSIC REPORT
                </button>
              </a>
            </div>
          </div>

          {/* Technical Framework - NEW SECTION */}
          <div className="service-category" style={{background: 'var(--nero-profondo)', border: '2px solid var(--oro-arcana)'}}>
            <div className="category-header">
              <Cpu className="category-icon" size={56} />
              <div>
                <h2 className="category-title">Our Technical Framework</h2>
                <p className="category-subtitle">
                  Proprietary infrastructure developed entirely in-house
                </p>
              </div>
            </div>
            <div className="category-content">
              <p className="category-description">
                Arcana Crypto operates through a proprietary recovery framework developed entirely in-house.
              </p>
              <p className="category-description">
                Our infrastructure integrates structured case assessment, controlled computational 
                methodologies, and purpose-built tooling designed specifically for legitimate wallet 
                access recovery scenarios.
              </p>
              <div className="service-list">
                <h3 className="service-list-title">This framework is:</h3>
                <ul className="column-list">
                  <li>Developed internally</li>
                  <li>Not outsourced</li>
                  <li>Not resold third-party software</li>
                  <li>Not dependent on automated mass-market tools</li>
                </ul>
              </div>
              <p className="category-description" style={{marginTop: '32px'}}>
                Each engagement is executed within a controlled technical environment and exclusively 
                on behalf of the verified rightful owner.
              </p>
              <p className="category-description" style={{
                fontSize: '20px', 
                color: 'var(--oro-arcana)', 
                fontWeight: '600',
                textAlign: 'center',
                marginTop: '32px',
                paddingTop: '32px',
                borderTop: '1px solid var(--border-subtle)'
              }}>
                Designed for precision. Executed with discipline.
              </p>
            </div>
          </div>

          {/* How We Work */}
          <div className="how-we-work">
            <h2 className="section-title" style={{marginTop: '100px'}}>How We Work</h2>
            <div className="steps-grid">
              <div className="step">
                <div className="step-number">01</div>
                <h3 className="step-title">Free Initial Assessment</h3>
                <p className="step-description">
                  Every case begins with a free, no-obligation technical assessment
                </p>
              </div>
              <div className="step">
                <div className="step-number">02</div>
                <h3 className="step-title">Technical Feasibility Check</h3>
                <p className="step-description">
                  We evaluate probability of success and identify appropriate service
                </p>
              </div>
              <div className="step">
                <div className="step-number">03</div>
                <h3 className="step-title">Clear Pricing & Probability</h3>
                <p className="step-description">
                  Transparent quote and realistic success probability provided
                </p>
              </div>
              <div className="step">
                <div className="step-number">04</div>
                <h3 className="step-title">Secure Execution</h3>
                <p className="step-description">
                  Work begins only after client approval with progress updates
                </p>
              </div>
              <div className="step">
                <div className="step-number">05</div>
                <h3 className="step-title">Verification & Delivery</h3>
                <p className="step-description">
                  Results delivered securely with optional post-recovery support
                </p>
              </div>
            </div>
          </div>

          {/* Pricing & Payment */}
          <div className="pricing-payment-section">
            <h2 className="section-title" style={{marginTop: '100px'}}>Pricing & Payment</h2>
            <div className="two-column">
              <div>
                <h3 className="column-title">PRICING TRANSPARENCY</h3>
                <ul className="column-list">
                  <li>Standard prices published on website</li>
                  <li>Variants depend on technical complexity</li>
                  <li>No hidden fees ever</li>
                  <li>Success-based fees only where applicable</li>
                  <li>Final pricing confirmed before work begins</li>
                </ul>
              </div>
              <div>
                <h3 className="column-title">PAYMENT OPTIONS</h3>
                <ul className="column-list">
                  <li>Fiat payments accepted (GBP, EUR, USD)</li>
                  <li>Cryptocurrency payments available</li>
                  <li>Escrow available for selected services</li>
                  <li>Flexible payment terms based on service type</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div style={{textAlign: 'center', marginTop: '100px'}}>
            <h2 className="cta-title" style={{fontSize: '42px', marginBottom: '32px'}}>
              Ready to Start?
            </h2>
            <p className="section-intro" style={{marginBottom: '40px'}}>
              All services begin with a free case assessment. No obligation, no upfront cost.
            </p>
            <a href="/#assessment">
              <button className="btn-primary" style={{fontSize: '20px', padding: '20px 48px'}}>
                START FREE ASSESSMENT
              </button>
            </a>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
            <p style={{fontSize: '13px', color: 'var(--grigio-medio)', marginTop: '12px'}}>
              <a href="mailto:legal@arcana-crypto.com" className="footer-email">
                legal@arcana-crypto.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
